import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { esPosterior } from '@/lib/dates';

/**
 * Estado LOCAL de los avisos de mensajes nuevos (Zustand + persistencia en localStorage).
 *
 * **Por qué no está en el backend**: la tabla `conversations` no tiene columna de «no leído» y el
 * proyecto decidió no inventarla (ver `docs/INBOX.md` §*Sin estado de “no leído”*). Así que «esta
 * conversación ya la vi» es una marca del NAVEGADOR, y vive aquí en vez de en un hook para que las
 * dos copias que hay en pantalla a la vez —el shell (`AppLayout`) y la pantalla Conversaciones—
 * vean lo mismo: abrir un chat apaga la burbuja del header y el punto de la fila sin recargar.
 *
 * **La clave es un «ámbito» opaco** que arma el hook: `<tenantId>:<userId>`. Multi-negocio y
 * multi-usuario en el mismo PC: cada combinación tiene su propio «ya visto» y su propia siembra, así
 * que un compañero que entra en el mismo navegador no hereda lo que otro ya leyó.
 *
 * Lo que se guarda por ámbito:
 *  - `vistos`: conversación → instante ISO del último mensaje dado por visto;
 *  - `sembrados`: la bandeja que había la PRIMERA vez se da por vista (si no, el estreno de la
 *    función mostraría una burbuja con todas las conversaciones de meses atrás).
 */

export interface InboxAlertsState {
  /** Ámbito → conversación → instante ISO hasta el que se dio por vista. */
  vistos: Record<string, Record<string, string>>;
  /** Ámbitos cuya bandeja inicial ya se sembró (una sola vez por ámbito). */
  sembrados: Record<string, boolean>;

  /** Da por vista la bandeja que había al estrenar los avisos (una vez por ámbito). */
  sembrar: (ambito: string, entradas: Record<string, string>) => void;
  /** Da por vista UNA conversación (se abrió su chat). No hace nada si ya estaba más al día. */
  marcarVisto: (ambito: string, conversationId: string, vistoEn: string) => void;
  /** Da por vistas varias conversaciones de golpe (botón «Marcar como visto» de la bandeja). */
  marcarVarios: (ambito: string, entradas: Record<string, string>) => void;
}

export const useInboxAlertsStore = create<InboxAlertsState>()(
  persist(
    (set) => ({
      vistos: {},
      sembrados: {},

      sembrar: (ambito, entradas) =>
        set((state) => ({
          sembrados: { ...state.sembrados, [ambito]: true },
          // Lo que ya estaba guardado gana: es igual o más reciente que la siembra.
          vistos: { ...state.vistos, [ambito]: { ...entradas, ...state.vistos[ambito] } },
        })),

      marcarVisto: (ambito, conversationId, vistoEn) =>
        set((state) => {
          const actual = state.vistos[ambito]?.[conversationId];

          // Sin cambio no se toca el estado: esto corre en cada refresco de la bandeja (cada 30 s) y
          // devolver un objeto nuevo re-renderizaría el shell entero en balde.
          if (actual && !esPosterior(vistoEn, actual)) return state;

          return {
            vistos: {
              ...state.vistos,
              [ambito]: { ...state.vistos[ambito], [conversationId]: vistoEn },
            },
          };
        }),

      marcarVarios: (ambito, entradas) =>
        set((state) => ({
          vistos: {
            ...state.vistos,
            // Mismo criterio que `marcarVisto`: la marca más reciente de cada conversación se queda.
            [ambito]: Object.entries(entradas).reduce<Record<string, string>>(
              (acumulado, [conversationId, vistoEn]) => {
                const actual = acumulado[conversationId];
                if (actual && !esPosterior(vistoEn, actual)) return acumulado;

                return { ...acumulado, [conversationId]: vistoEn };
              },
              { ...state.vistos[ambito] },
            ),
          },
        })),
    }),
    {
      name: 'wa-sales.inbox-alerts',
      version: 1,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
